<script setup lang="ts">
import MainBreadcrumbs from "@/ui/components/header/MainBreadcrumbs.vue";
import { ref, watch } from "vue";
import { useApiClient } from "@/api/client";
import DocumentAlertListViewDTO = App.Data.Alerts.DocumentAlertListViewDTO;
import dayjs from "dayjs";
import CheckInterval from "@/modules/alerts/components/CheckInterval.vue";
import { t } from "@/plugins/i18n";
import { debounce } from "vue-debounce";
import { useInterval } from "@vueuse/core";
import formatItemSettings from "../components/formatItemSettings";
import DeleteAlertDialog from "@/modules/alerts/components/DeleteAlertDialog.vue";
import useDialog from "@/ui/composables/useDialog";
import router from "@/router/router";

const breadcrumbs = [
	{
		title: "Alerts",
		disabled: true,
		to: "/alerts",
	},
];

const headers = [
	{ title: "Configuration", key: "stored_document", sortable: false },
	{ title: "Settings", key: "settings", sortable: false },
	{ title: "Active", key: "is_active", sortable: true },
	{ title: "Check Interval", key: "check_interval_type", sortable: false },
	{ title: "Last Checked", key: "last_check.updated_at" },
	{ title: "Message", key: "last_check.message", sortable: false },
	{ title: "In Alert", key: "last_check.is_in_alert" },
	{ title: "Actions", key: "actions" },
];

const isLoading = ref(false);
const items = ref([]);
const itemsPerPage = ref(100);
const totalItemCount = ref(0);
const page = ref(1);
const sortBy = ref([]);

const loadItems = debounce(async ({ page, itemsPerPage, sortBy }) => {
	isLoading.value = true;

	const client = useApiClient("alerts");

	const response = await client.get({ page, itemsPerPage, sortBy });
	if (response.isOk()) {
		items.value = response.value.data;
		totalItemCount.value = response.value.total;
	}

	isLoading.value = false;
}, 300);

const ctr = useInterval(10000);
watch(ctr, () => {
	loadItems({
		page: page.value,
		itemsPerPage: itemsPerPage.value,
		sortBy: sortBy.value,
	});
});

const alertToDelete = ref<DocumentAlertListViewDTO | null>(null);
const isDeleteDialogVisible = ref(false);
const addAlert = async () => {
	const confirm = await useDialog().confirm({
		title: "Open Explorer",
		message:
			"This will redirect you to the explorer, where you can define the new alert, save it and return to the alerts page",
	});

	if (!confirm) return;

	router.push({
		name: "explorer.metricWrapper",
		query: {
			fresh: "true",
			"redirect-to-alerts": "true",
		},
	});
};

const removeAlert = (alert: DocumentAlertListViewDTO) => {
	alertToDelete.value = alert;
	isDeleteDialogVisible.value = true;
};
</script>

<template>
	<delete-alert-dialog
		v-model="isDeleteDialogVisible"
		:alert-id="alertToDelete?.id ?? null"
		:alert-title="alertToDelete?.stored_document.title ?? ''"
		@deleted="ctr++"
	/>
	<main-breadcrumbs :breadcrumbs="breadcrumbs" />
	<v-card class="px-4 py-2">
		<div class="d-flex pa-4">
			<v-spacer />
			<v-btn variant="flat" color="primary" @click="addAlert()">
				<i class="fa-duotone fa-plus fa-lg mr-3"></i> Add new alert
			</v-btn>
		</div>
		<v-data-table-server
			v-model:items-per-page="itemsPerPage"
			v-model:page="page"
			v-model:sort-by="sortBy"
			:headers="headers"
			:items="items"
			:items-length="totalItemCount"
			:loading="isLoading"
			@update:options="loadItems"
		>
			<template v-slot:item.stored_document="{ item }">
				<router-link
					class="router-link-primary"
					:to="{ name: 'alerts.view', params: { id: item.id } }"
				>
					{{ t("alerts.actions.view") }} {{ item.stored_document.title }}
				</router-link>
			</template>
			<template v-slot:item.settings="{ item }">
				{{ formatItemSettings(item) }}
			</template>
			<template v-slot:item.is_active="{ item }">
				<v-chip
					:color="item.is_active ? 'primary' : 'on-surface'"
					label
					density="comfortable"
				>
					{{ item.is_active ? "Active" : "Inactive" }}
				</v-chip>
			</template>
			<template v-slot:item.notification_type_name_clean="{ item }">
				{{
					t(`notifications.types.${item.notification_type_name_clean}.title`)
				}}
			</template>
			<template v-slot:item.check_interval_type="{ item }">
				<check-interval
					:check-interval-type="item.check_interval_type"
					:check-interval-specification="item.check_interval_specification"
				/>
			</template>
			<template v-slot:item.last_check.updated_at="{ item }">
				<template v-if="item.last_check === null"> -</template>
				<v-icon
					v-else-if="item.last_check.is_in_alert === null"
					icon="fa fa-spin fa-spinner"
				/>
				<template v-else
					>{{ dayjs(item.last_check.updated_at).format("DD MMM YYYY HH:mm") }}
				</template>
			</template>
			<template v-slot:item.last_check.is_in_alert="{ item }">
				<v-chip
					:color="item.last_check?.is_in_alert ? 'primary' : 'on-surface'"
					label
					density="comfortable"
				>
					{{ item.last_check?.is_in_alert ? "True" : "False" }}
				</v-chip>
			</template>
			<template v-slot:item.actions="{ item }">
				<v-btn
					size="32"
					icon
					color="error"
					variant="text"
					rounded
					@click="removeAlert(item)"
					:data-testid="`delete-${item.stored_document.title}`"
				>
					<v-icon size="16">fa-light fa-trash-can</v-icon>
				</v-btn>
			</template>
		</v-data-table-server>
	</v-card>
</template>

<style scoped lang="scss"></style>
